import type { QueryKey } from '@tanstack/react-query';

import { getSnippetsBaseQueryKey } from '../../../../hooks/api/snippets/shared';
import { useQuery } from '../../../../ReactQuery';
import { get } from '../../../../redux/api';
import { nuc } from '../../../../utils';
import type { GetSnippetsParams, SnippetsResponse, SnippetStatus } from '../../../../../shared/snippets';

export type UseComparedSnippetCountsOptions = {
  userRevisionLocator: string;
  olderRevisionLocator: string;
};

export type ComparedSnippetCount = {
  isLoading: boolean;
  count: number;
};
export type ComparedSnippetCounts = Record<SnippetStatus, ComparedSnippetCount>;

/**
 * Function that fetches compared snippets based on the given options.
 */
function getComparedSnippets(
  { userRevisionLocator, olderRevisionLocator }: UseComparedSnippetCountsOptions,
  status: SnippetStatus,
) {
  const params: GetSnippetsParams = {
    // Hardcode the path to root since we want to count all of the snippets.
    path: '/',
  };
  return get<SnippetsResponse>(`/api/revisions/${nuc(userRevisionLocator)}/snippets/compare/${nuc(olderRevisionLocator)}/${status}`, false, params);
}

/**
 * Returns the query key for the snippets based on the given options.
 */
const getComparedSnippetsQueryKey = (
  { userRevisionLocator, olderRevisionLocator }: UseComparedSnippetCountsOptions,
  status: SnippetStatus,
): QueryKey => {
  const params: GetSnippetsParams = {
    // Hardcode the path to root since we want to count all of the snippets.
    path: '/',
  };
  return [...getSnippetsBaseQueryKey(userRevisionLocator), 'compare', olderRevisionLocator, status, params];
};

/**
 * useComparedSnippetCounts is a hook that fetches compared snippet count
 * for each SnippetStatus.
 */
export default function useComparedSnippetCounts(options: UseComparedSnippetCountsOptions): ComparedSnippetCounts {
  const newResponse = useQuery<SnippetsResponse>({
    queryKey: getComparedSnippetsQueryKey(options, 'new'),
    queryFn: () => getComparedSnippets(options, 'new'),
  });

  const removedResponse = useQuery<SnippetsResponse>({
    queryKey: getComparedSnippetsQueryKey(options, 'removed'),
    queryFn: () => getComparedSnippets(options, 'removed'),
  });

  const unchangedResponse = useQuery<SnippetsResponse>({
    queryKey: getComparedSnippetsQueryKey(options, 'unchanged'),
    queryFn: () => getComparedSnippets(options, 'unchanged'),
  });

  return {
    new: {
      isLoading: newResponse.isLoading,
      count: newResponse.data?.totalCount ?? 0,
    },
    removed: {
      isLoading: removedResponse.isLoading,
      count: removedResponse.data?.totalCount ?? 0,
    },
    unchanged: {
      isLoading: unchangedResponse.isLoading,
      count: unchangedResponse.data?.totalCount ?? 0,
    },
  };
}
